import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '@core/services/api.service';
import { AuthService } from '@core/services/auth.service';
import { HeaderService } from '@core/services/header.service';
import { SnackbarService } from '@core/services/snackbar.service';

@Component({
  standalone: false,
  selector: 'app-change-requests',
  templateUrl: './change-requests.component.html',
  styleUrls: ['./change-requests.component.scss']
})
export class ChangeRequestsComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  loading = true;
  activeTab: 'my' | 'team' | 'all' = 'my';

  myRequests: any[] = [];
  teamRequests: any[] = [];
  allRequests: any[] = [];
  shifts: any[] = [];
  clients: any[] = [];
  teamLoaded = false;
  allLoaded = false;

  myUserId = '';
  statusFilter = 'all';
  busy: { [id: string]: boolean } = {};

  showForm = false;
  form: any = this.emptyForm();
  submitting = false;

  statusOptions = [
    { label: 'All', value: 'all' },
    { label: 'Pending', value: 'pending' },
    { label: 'Approved', value: 'approved' },
    { label: 'Rejected', value: 'rejected' },
  ];

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private headerService: HeaderService,
    private snackbar: SnackbarService,
  ) {}

  ngOnInit(): void {
    this.headerService.setTitle('Roster Requests');
    this.myUserId = this.authService.getCurrentUser()?.id || '';
    this.load();
  }

  emptyForm(): any {
    return { type: 'roster', targetId: '', reason: '', effectiveDate: new Date().toISOString().split('T')[0] };
  }

  isManagerOrHr(): boolean {
    const role = (this.authService.getCurrentUser()?.role || '').toLowerCase().replace(/^role_/, '');
    return ['admin', 'hr', 'hr_admin', 'super_admin', 'manager'].includes(role);
  }
  isHrAdmin(): boolean {
    const role = (this.authService.getCurrentUser()?.role || '').toLowerCase().replace(/^role_/, '');
    return ['admin', 'hr', 'hr_admin', 'super_admin'].includes(role);
  }

  private normalize(res: any): any[] {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.data)) return res.data;
    return [];
  }

  load(): void {
    this.loading = true;
    forkJoin({
      mine:    this.apiService.get('/change-requests/my').pipe(catchError(() => of([]))),
      shifts:  this.apiService.get('/shifts').pipe(catchError(() => of([]))),
      clients: this.apiService.get('/clients').pipe(catchError(() => of([]))),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (d: any) => {
        this.myRequests = this.normalize(d.mine);
        this.shifts = this.normalize(d.shifts);
        this.clients = this.normalize(d.clients);
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  switchTab(tab: 'my' | 'team' | 'all'): void {
    this.activeTab = tab;
    if (tab === 'team' && !this.teamLoaded) this.loadTeam();
    if (tab === 'all' && !this.allLoaded) this.loadAll();
  }

  loadTeam(): void {
    this.loading = true;
    this.apiService.get('/change-requests/team').pipe(catchError(() => of([]))).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.teamRequests = this.normalize(res);
      this.teamLoaded = true;
      this.loading = false;
    });
  }

  loadAll(): void {
    this.loading = true;
    this.apiService.get('/change-requests').pipe(catchError(() => of([]))).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.allRequests = this.normalize(res);
      this.allLoaded = true;
      this.loading = false;
    });
  }
  get filteredAll(): any[] {
    return this.allRequests.filter(r => this.statusFilter === 'all' || (r.status || '').toLowerCase() === this.statusFilter);
  }
  get pendingAllCount(): number {
    return this.allRequests.filter(r => (r.status || '').toLowerCase() === 'pending').length;
  }

  get filteredTeam(): any[] {
    return this.teamRequests
      .filter(r => this.statusFilter === 'all' || (r.status || '').toLowerCase() === this.statusFilter);
  }
  get pendingTeamCount(): number {
    return this.teamRequests.filter(r => (r.status || '').toLowerCase() === 'pending').length;
  }

  shiftName(id: string): string {
    const s = this.shifts.find(x => x.id === id);
    return s ? `${s.name} (${(s.startTime || '').slice(0, 5)}–${(s.endTime || '').slice(0, 5)})` : (id || '—');
  }
  clientName(id: string): string { return this.clients.find(c => c.id === id)?.name || id || '—'; }

  targetDisplay(r: any): string {
    if (r.targetLabel) return r.targetLabel;
    return r.type === 'roster' ? this.shiftName(r.targetId) : this.clientName(r.targetId);
  }
  typeLabel(t: string): string { return t === 'roster' ? 'Shift / Roster' : 'Client'; }
  statusClass(s: string): string {
    switch ((s || '').toLowerCase()) {
      case 'approved': return 'cq-st-green';
      case 'rejected': return 'cq-st-red';
      default:         return 'cq-st-amber';
    }
  }
  initials(name: string): string { return (name || '?').split(' ').map(n => n.charAt(0)).join('').slice(0, 2).toUpperCase(); }

  openNew(): void { this.form = this.emptyForm(); this.showForm = true; }
  closeForm(): void { this.showForm = false; }
  onTypeChange(): void { this.form.targetId = ''; }

  submit(): void {
    if (!this.form.targetId) { this.snackbar.showFailure(`Select a ${this.form.type === 'roster' ? 'shift' : 'client'}`); return; }
    if (!this.form.reason?.trim()) { this.snackbar.showFailure('Please add a reason'); return; }

    this.submitting = true;
    const targetLabel = this.form.type === 'roster' ? this.shiftName(this.form.targetId) : this.clientName(this.form.targetId);
    this.apiService.post('/change-requests', {
      type: this.form.type,
      targetId: this.form.targetId,
      targetLabel,
      reason: this.form.reason.trim(),
      effectiveDate: this.form.type === 'roster' ? this.form.effectiveDate : null,
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.submitting = false;
        this.showForm = false;
        this.snackbar.showSuccess('Request submitted to your manager');
        this.load();
      },
      error: (err: any) => {
        this.submitting = false;
        this.snackbar.showFailure(err?.error?.message || 'Could not submit request');
      }
    });
  }

  withdraw(r: any): void {
    if (!r?.id) return;
    this.busy[r.id] = true;
    this.apiService.delete(`/change-requests/${r.id}`).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.busy[r.id] = false; this.snackbar.showSuccess('Request withdrawn'); this.load(); },
      error: () => { this.busy[r.id] = false; this.snackbar.showFailure('Could not withdraw'); }
    });
  }

  decide(r: any, status: 'approved' | 'rejected'): void {
    if (!r?.id) return;
    this.busy[r.id] = true;
    this.apiService.patch(`/change-requests/${r.id}/decision`, { status }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        r.status = status;
        this.busy[r.id] = false;
        this.snackbar.showSuccess(status === 'approved' ? 'Approved & applied' : 'Request rejected');
        if (this.teamLoaded) this.loadTeam();
        if (this.allLoaded) this.loadAll();
      },
      error: () => { this.busy[r.id] = false; this.snackbar.showFailure('Action failed'); }
    });
  }
}
